<template>
  <div class="subscription-payment">
    <el-card class="payment-card">
      <div class="order-header">
        <h2>Subscription Order Details</h2>
        <div class="order-status" :class="orderInfo.status.toLowerCase()">
          {{ orderInfo.status }} PAYMENT
        </div>
      </div>

      <div class="order-content">
        <div class="content-info">
          <div class="content-details">
            <span class="content-type">LearningFirst Platform Subscription</span>
            <h3>{{ orderInfo.quantity }} Student Seats × {{ orderInfo.duration_months }} Months</h3>
            <p class="price">${{ formatPrice(orderInfo.unit_price) }} per student/month</p>
          </div>
        </div>

        <div class="order-details">
          <div class="detail-item">
            <span class="label">Order ID:</span>
            <span class="value">{{ orderInfo._id?.$id || '-' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Quantity:</span>
            <span class="value">{{ orderInfo.quantity }} students</span>
          </div>
          <div class="detail-item">
            <span class="label">Duration:</span>
            <span class="value">{{ orderInfo.duration_months }} months</span>
          </div>
          <div class="detail-item">
            <span class="label">Per Student Total:</span>
            <span class="value">${{ formatPrice(orderInfo.total_price_per_student) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Total Amount:</span>
            <span class="value total-amount">${{ formatPrice(orderInfo.total_price) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Order Date:</span>
            <span class="value">{{ formatDate(orderInfo.order_time * 1000) }}</span>
          </div>
        </div>

        <div class="payment-section" v-if="orderInfo.status === 'PENDING'">
          <div class="payment-method">
            <h4>Pay by Credit Card</h4>
            <el-button
              type="primary"
              @click="processPayment"
              :loading="processing"
            >
              Pay Now by Credit Card
            </el-button>
          </div>

        </div>

        <div class="payment-section">
          <router-link to="/home/subscriptionOrders" class="view-orders-link">
            → View My Subscription Orders
          </router-link>
        </div>
      </div>
    </el-card>
    <div class="cancel-order">
      <el-button type="text" @click="cancelOrder" :loading="cancelling" v-if="orderInfo.status === 'PENDING'">
        Cancel this order
      </el-button>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'

export default {
  name: 'SubscriptionPayment',
  data() {
    return {
      orderInfo: {
        status: 'PENDING',
        _id: null,
        quantity: 0,
        duration_months: 0,
        unit_price: 0,
        total_price_per_student: 0,
        total_price: 0,
        order_time: null
      },
      processing: false,
      cancelling: false
    }
  },
  created() {
    this.fetchOrderInfo()
  },
  methods: {
    async fetchOrderInfo() {
      try {
        const response = await postRequest('getSubscriptionOrder', {
          order_id: this.$route.query.order_id
        })
        if (response.data.code === 0) {
          this.orderInfo = response.data.data
        }
      } catch (error) {
        console.error('Error fetching order info:', error)
        this.$message.error('Failed to load order information')
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleString()
    },
    processPayment() {
      this.processing = true
      try {
        this.$router.push({
          path: '/home/subscriptionPay',
          query: {
            order_id: this.$route.query.order_id
          }
        })
      } catch (error) {
        console.error('Payment error:', error)
        this.$message.error('Failed to process payment')
      } finally {
        this.processing = false
      }
    },
    async cancelOrder() {
      try {
        await this.$confirm(
          'Are you sure you want to cancel this order?',
          'Cancel Order',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        )

        this.cancelling = true
        const response = await postRequest('cancelSubscriptionOrder', {
          order_id: this.$route.query.order_id
        })

        if (response.data.code === 0) {
          this.$message.success('Order cancelled successfully')
          await this.fetchOrderInfo()
        }
      } catch (error) {
        if (error === 'cancel') return
        console.error('Cancel error:', error)
        this.$message.error('Failed to cancel order')
      } finally {
        this.cancelling = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.subscription-payment {
  padding: 20px;

  .payment-card {
    max-width: 800px;
    margin: 0 auto;

    .order-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h2 {
        margin: 0;
      }

      .order-status {
        padding: 4px 12px;
        border-radius: 4px;
        font-weight: 500;

        &.pending {
          background-color: #fdf6ec;
          color: #e6a23c;
        }

        &.completed {
          background-color: #f0f9eb;
          color: #67c23a;
        }
      }
    }

    .content-info {
      display: flex;
      gap: 20px;
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid #ebeef5;

      .content-details {
        .content-type {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          margin-bottom: 8px;
          background-color: #FFF3E6;
          color: #FF8833;
        }

        h3 {
          margin: 0 0 8px;
          color: #303133;
        }

        .price {
          color: #f56c6c;
          font-weight: 500;
          margin: 0;
        }
      }
    }

    .order-details {
      margin-bottom: 24px;

      .detail-item {
        display: flex;
        margin-bottom: 12px;

        .label {
          width: 180px;
          color: #909399;
        }

        .value {
          color: #303133;
          font-weight: 500;

          &.total-amount {
            font-size: 18px;
            color: #FF6450;
            font-weight: 700;
          }

          &.pending-review {
            color: #e6a23c;
            background-color: #fdf6ec;
            padding: 2px 8px;
            border-radius: 4px;
          }

          &.approved {
            color: #67c23a;
            background-color: #f0f9eb;
            padding: 2px 8px;
            border-radius: 4px;
          }

          &.rejected {
            color: #f56c6c;
            background-color: #fef0f0;
            padding: 2px 8px;
            border-radius: 4px;
          }

          a {
            color: #409EFF;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    .payment-section {
      .payment-method {
        margin-bottom: 24px;

        h4 {
          margin: 0 0 12px;
          color: #909399;
          font-size: 14px;
          padding-bottom: 8px;
          border-bottom: 1px solid #ebeef5;
        }
      }

      .view-orders-link {
        display: block;
        text-align: right;
        color: #FF8833;
        text-decoration: none;
        margin-top: 16px;
        font-size: 14px;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.cancel-order {
  text-align: center;
  margin-top: 16px;

  .el-button {
    color: #909399;
    font-weight: normal;
  }
}
</style>
